import { useEffect, useState, useCallback, useContext, useMemo } from "react";
import { Card, Row, Col, Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ProfileContext } from "pages/Profile";

export default function UserDetails() {

  const { profile, setProfile } = useContext(ProfileContext)
  const { user: authUser } = useSelector(x => x.auth)

  const url = process.env.REACT_APP_URL

  const [fields, setFields] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    website: '',
  })
  const [editingField, setEditingField] = useState(null)
  const [isSavingField, setIsSavingField] = useState(false)

  useEffect(() => {
    setFields({
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      phone: profile?.phone || '',
      website: profile?.website || '',
    })
  }, [profile])

  const canEdit = useMemo(
    () => (authUser.user.acl === 'Officer' || authUser.user._id === profile._id),
    [authUser.user.acl, authUser.user._id, profile._id]
  )

  const createdAtDisplay = profile?.createdAt
    ? new Date(profile.createdAt).toLocaleString()
    : 'N/A';

  const updateField = (key, value) => {
    setFields((prev) => ({ ...prev, [key]: value }))
  }

  const persistProfileDetails = useCallback(async (partial = null) => {
    if (!canEdit) return;

    const nextFields = partial ? { ...fields, ...partial } : fields;
    setIsSavingField(true);
    try {
      const data = {
        firstName: nextFields.firstName,
        lastName: nextFields.lastName,
        phone: nextFields.phone,
        website: nextFields.website,
      };

      const response = await fetch(`${url}/users/${profile._id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUser.token}`
        },
        body: JSON.stringify(data)
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.Error || 'Failed to update user details');
      }
      setProfile(prev => ({ ...prev, ...result, avatarUrl: prev?.avatarUrl || result?.avatarUrl || null, avatar: prev?.avatar || result?.avatar || null }));
      toast.success('The user details have been updated', { position: 'bottom-left' });
    } catch (error) {
      toast.error('Failed to update user details', { position: 'bottom-left' });
      console.error(error);
    } finally {
      setIsSavingField(false);
    }
  }, [canEdit, fields, url, profile._id, authUser.token, setProfile]);

  const startEditing = (fieldKey) => {
    if (!canEdit || isSavingField) return;
    setEditingField(fieldKey);
  }

  const finishEditing = async () => {
    if (!editingField || isSavingField) return;
    await persistProfileDetails();
    setEditingField(null);
  }

  const cancelEditing = () => {
    setFields({
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      phone: profile?.phone || '',
      website: profile?.website || '',
    })
    setEditingField(null)
  }

  const renderEditableRow = ({ key, label, value, type = 'text', placeholder = '' }) => {
    const isEditing = editingField === key;

    return (
      <div className="settings-inline-row" key={key}>
        <span className="settings-inline-label">{label}</span>
        {isEditing ? (
          <Form.Control
            autoFocus
            type={type}
            className="settings-inline-input"
            value={value}
            placeholder={placeholder}
            onChange={(e) => updateField(key, e.target.value)}
            onBlur={finishEditing}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                finishEditing();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                cancelEditing();
              }
            }}
          />
        ) : (
          <button
            type="button"
            className="settings-inline-value"
            onClick={() => startEditing(key)}
            disabled={!canEdit || isSavingField}
          >
            {value || 'N/A'}
          </button>
        )}
      </div>
    )
  }

  return (
    <Card className="mt-3 mb-3 user-card settings-card meetings-mock-card-shell meetings-page-card">
      <Card.Header className="settings-card-header">
        <Row className="mt-2 mb-2"><Col>Profile Details</Col></Row>
      </Card.Header>

      <Card.Body className="settings-card-body">
        <div className="settings-inline-list">
          {renderEditableRow({ key: 'firstName', label: 'First Name', value: fields.firstName, placeholder: 'First Name' })}
          {renderEditableRow({ key: 'lastName', label: 'Last Name', value: fields.lastName, placeholder: 'Last Name' })}
          {renderEditableRow({ key: 'phone', label: 'Phone', value: fields.phone, placeholder: 'Phone' })}
          {renderEditableRow({ key: 'website', label: 'Website', value: fields.website, placeholder: 'https://yourwebsite.com' })}

          <div className="settings-inline-row">
            <span className="settings-inline-label">Created</span>
            <span className="settings-inline-static">{createdAtDisplay}</span>
          </div>
        </div>
      </Card.Body>

      {isSavingField && (
        <Card.Footer className="settings-card-footer">
          <Row className="mt-2 mb-2"><Col className="text-end">Saving profile...</Col></Row>
        </Card.Footer>
      )}
    </Card >
  )
}